import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Spinner from './components/ui/Spinner';
import ResetPasswordModal from './components/ResetPasswordModal';

const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const CalendarHub = lazy(() => import('./pages/Calendar'));
const Composer = lazy(() => import('./pages/Composer'));
const Account = lazy(() => import('./pages/Account'));
const Report = lazy(() => import('./pages/Report'));
const Settings = lazy(() => import('./pages/Settings'));
const ChatWizard = lazy(() => import('./pages/ChatWizard'));
const Negocios = lazy(() => import('./pages/Negocios'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Admin = lazy(() => import('./pages/Admin'));

const HomeRedirect = () => {
  const { currentBusiness, allBusinesses, loading, user } = useAuth();
  
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Spinner />
    </div>
  );

  if (!user) return <Navigate to="/login" replace />;

  if (!allBusinesses || allBusinesses.length === 0) {
    return <Navigate to="/empezar" replace />;
  }

  return <Navigate to="/negocios" replace />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Spinner />
    </div>
  );

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Spinner />
    </div>
  );

  if (!user) return <Navigate to="/login" replace />;
  // Solo administradores designados. Los demás no ven ni acceden al panel.
  if (!profile?.is_admin) return <Navigate to="/inicio" replace />;

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Spinner />
    </div>
  );

  if (user) {
    return <Navigate to="/inicio" replace />;
  }

  return children;
};

export default function App() {
  return (
    <AuthProvider>
      <ResetPasswordModal />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><Spinner /></div>}>
        <Routes>
          <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/inicio" element={<HomeRedirect />} />
            <Route path="/empezar" element={<Onboarding />} />
            <Route path="/n/:bizId/estrategia" element={<ChatWizard />} />
            <Route path="/n/:bizId/calendario" element={<CalendarHub />} />
            <Route path="/n/:bizId/post/:postId" element={<Composer />} />
            <Route path="/n/:bizId/reporte/:mes" element={<Report />} />
            <Route path="/n/:bizId/ajustes" element={<Settings />} />
            <Route path="/negocios" element={<Negocios />} />
            <Route path="/cuenta" element={<Account />} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          </Route>

          <Route path="*" element={<Navigate to="/inicio" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}